import { useEffect, useMemo, useState, SyntheticEvent, ChangeEvent } from "react";
import { Box, Button, Card, CardContent, Divider, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import axios from "axios";
import moment from "moment-timezone";
import Title from "../../components/Title";
import HeaderResponsive from "../../components/layouts/HeaderResponsive";
import PageScrollArea from "../../components/layouts/PageScrollArea";
import EmptyStateMessage from "../../components/layouts/EmptyStateMessage";
import SimpleCustomTable from "../../components/table/SimpleCustomTable";
import { SimpleColumn, SimpleColumnGroup, SimpleRow } from "../../components/table/interface";
import { useDrawer } from "../../components/context/DrawerContext";
import { stickyListRegionWidth } from "../../components/layouts/StickyListToolbar";
import { nowOperationalDateString } from "../../utils/operationalDateTime";
import { isSuperAdminUser, parseUserFromApiResponse } from "../../utils/authScope";
import { Office } from "../../models/office";
import { Collect } from "../../models/collect";
import CollectFilter from "../../components/filter/collectbyDate";

interface TransmissionRowApi {
  office_id: number;
  office_name: string;
  collect_id: number;
  collect_name: string;
  operated_days: number;
  total_days: number;
  transmission_percentage: number;
  daily_status: Record<string, string>;
}

interface TransmissionResponse {
  data: TransmissionRowApi[];
  dates: string[];
  meta?: {
    total?: number;
  };
}

interface ReporteTransmisionProps {
  showAlert: (
    message: string,
    severity?: "error" | "info" | "success" | "warning"
  ) => void;
  showLoading: () => void;
  hideLoading: () => void;
}

const firstDayOfCurrentMonth = () =>
  moment.tz(nowOperationalDateString(), "YYYY-MM-DD", "America/Bogota").startOf("month").format("YYYY-MM-DD");

const compareOfficeCollect = (a: { office_name?: unknown; collect_name?: unknown }, b: { office_name?: unknown; collect_name?: unknown }) => {
  const byOffice = String(a.office_name ?? "").localeCompare(String(b.office_name ?? ""), "es", {
    sensitivity: "base",
  });
  if (byOffice !== 0) return byOffice;
  return String(a.collect_name ?? "").localeCompare(String(b.collect_name ?? ""), "es", { sensitivity: "base" });
};

const ReporteTransmision: React.FC<ReporteTransmisionProps> = ({
  showAlert,
  showLoading,
  hideLoading,
}) => {
  document.title = "Informe Transmisión";
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { drawerOpen } = useDrawer();

  const [authUser, setAuthUser] = useState<{ is_system?: unknown; username?: unknown } | null>(null);
  const [authResolved, setAuthResolved] = useState(false);
  const [offices, setOffices] = useState<Office[]>([]);
  const [collectsOffice, setCollectsOffice] = useState<Collect[]>([]);
  const [office_id, setOffice_id] = useState("");
  const [selectedCollects, setSelectedCollects] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [start_date, setStart_date] = useState(firstDayOfCurrentMonth());
  const [end_date, setEnd_date] = useState(nowOperationalDateString());
  const [dates, setDates] = useState<string[]>([]);
  const [rows, setRows] = useState<SimpleRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [todo, setTodo] = useState(false);

  const isSuperAdmin = isSuperAdminUser(authUser);
  const filtersDisabledForTodo = isSuperAdmin && todo;

  useEffect(() => {
    axios.get("offices").then(({ data }) => setOffices(data.data));
  }, []);

  useEffect(() => {
    const run = async () => {
      try {
        const { data } = await axios.get("user");
        setAuthUser(parseUserFromApiResponse(data));
      } catch {
        setAuthUser(null);
      } finally {
        setAuthResolved(true);
      }
    };
    void run();
  }, []);

  useEffect(() => {
    if (!isSuperAdmin) {
      setTodo(false);
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    if (office_id) {
      axios.get(`collectsoffice/${office_id}`).then(({ data }) => {
        setCollectsOffice(data);
        setSelectedCollects([]);
        setSelectAll(false);
      });
    }
  }, [office_id]);

  const handleCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSelectedCollects((prev) => (prev.includes(value) ? prev.filter((id) => id !== value) : [...prev, value]));
  };

  const handleSelectAll = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const allIds = collectsOffice.map((collect) => collect.id.toString());
      setSelectedCollects(allIds);
      setSelectAll(true);
    } else {
      setSelectedCollects([]);
      setSelectAll(false);
    }
  };

  const handleTodoChange = (event: ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setTodo(checked);
    if (checked) {
      setOffice_id("");
      setSelectedCollects([]);
      setSelectAll(false);
      setCollectsOffice([]);
    }
  };

  const validateForm = () => {
    if (!authResolved) {
      showAlert("Espere a que termine la carga de su sesión.", "warning");
      return false;
    }

    if (!start_date || !end_date) {
      showAlert("Debe seleccionar el rango de fechas.", "warning");
      return false;
    }

    if (start_date > end_date) {
      showAlert("La fecha inicio no puede ser mayor a la fecha fin.", "warning");
      return false;
    }

    if (isSuperAdmin && todo) {
      return true;
    }

    if (!office_id) {
      showAlert("Debe seleccionar una oficina.", "warning");
      return false;
    }

    if (!selectedCollects || selectedCollects.length === 0) {
      showAlert("Debe seleccionar al menos un cobro.", "warning");
      return false;
    }

    return true;
  };

  const handleSubmit = async (event: SyntheticEvent) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    showLoading();
    setLoading(true);
    try {
      const payload: Record<string, unknown> = {
        start_date: start_date,
        end_date: end_date,
      };

      const scopeAllSystem = isSuperAdmin && todo;
      if (!scopeAllSystem) {
        payload.office_id = Number(office_id);
        payload.collect_ids = selectedCollects.map((id) => Number(id));
      }

      const response = await axios.post<TransmissionResponse>("/inform/transmission", payload);
      const apiRows = Array.isArray(response.data?.data) ? response.data.data : [];
      const apiDates = Array.isArray(response.data?.dates) ? response.data.dates : [];

      const mappedRows: SimpleRow[] = apiRows
        .map((row) => {
          const dynamicStatus = apiDates.reduce<Record<string, string>>((acc, date) => {
            acc[`day_${date}`] = row.daily_status?.[date] || "";
            return acc;
          }, {});

          return {
            id: `${row.office_id}-${row.collect_id}`,
            office_name: row.office_name,
            collect_name: row.collect_name,
            operated_days: row.operated_days,
            total_days: row.total_days,
            transmission_percentage: row.transmission_percentage,
            ...dynamicStatus,
          };
        })
        .sort(compareOfficeCollect);

      setDates(apiDates);
      setRows(mappedRows);

      if (mappedRows.length === 0) {
        showAlert("No se encontraron datos para el rango seleccionado.", "info");
      } else {
        showAlert(`Se encontraron ${mappedRows.length} cobros con datos de transmisión.`, "success");
      }
    } catch (error) {
      console.error("Error obteniendo informe de transmisión:", error);
      showAlert("No fue posible obtener el informe de transmisión.", "error");
      setDates([]);
      setRows([]);
    } finally {
      hideLoading();
      setLoading(false);
    }
  };

  const { columns, columnGroups } = useMemo(() => {
    const baseColumns: SimpleColumn[] = [
      { field: "office_name", headerName: "Oficina", minWidth: 150, width: 180, wrapText: true },
      { field: "collect_name", headerName: "Cobro", minWidth: 130, width: 160, wrapText: true },
      { field: "operated_days", headerName: "Días Operó", minWidth: 90, width: 100, align: "center" },
      { field: "total_days", headerName: "Días Rango", minWidth: 90, width: 100, align: "center" },
      {
        field: "transmission_percentage",
        headerName: "% Transmisión",
        minWidth: 110,
        width: 120,
        align: "center",
        renderCell: ({ row }) => `${Number(row.transmission_percentage ?? 0).toFixed(2)}%`,
      },
    ];

    const dateColumns: SimpleColumn[] = dates.map((date) => ({
      field: `day_${date}`,
      headerName: moment.tz(date, "YYYY-MM-DD", "America/Bogota").format("DD/MM"),
      minWidth: 100,
      width: 108,
      align: "center",
      wrapText: true,
      renderCell: ({ row }) => row[`day_${date}`] || "",
    }));

    const mergedColumns = [...baseColumns, ...dateColumns];
    const groups: SimpleColumnGroup[] =
      dateColumns.length > 0
        ? [{ header: "Operación por Día", fields: dateColumns.map((column) => column.field) }]
        : [];

    return { columns: mergedColumns, columnGroups: groups };
  }, [dates]);

  return (
    <HeaderResponsive sx={{ py: 0, px: 0.5 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
        <Title>Informe Transmisión</Title>
        <Button
          type="submit"
          form="form-transmision"
          variant="contained"
          disabled={!authResolved}
          startIcon={!isMobile && <SearchIcon />}
        >
          {isMobile ? <SearchIcon /> : "Buscar"}
        </Button>
      </Box>

      <Card
        sx={{
          width: (t) => stickyListRegionWidth(t, drawerOpen),
          maxWidth: "100%",
          boxSizing: "border-box",
          overflowX: "auto",
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        <form id="form-transmision" onSubmit={handleSubmit}>
          <CardContent
            sx={{
              width: (t) => stickyListRegionWidth(t, drawerOpen),
              maxWidth: "100%",
              boxSizing: "border-box",
              overflowX: "auto",
              flexShrink: 0,
              scrollbarColor: '#0768c9 #d9e1ea',
              '&::-webkit-scrollbar': {
                width: 12,
                height: 12,
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: '#d9e1ea',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#0768c9',
                borderRadius: 999,
                border: '2px solid #d9e1ea',
              },
              '&::-webkit-scrollbar-corner': {
                backgroundColor: '#d9e1ea',
              },
            }}
          >
            <CollectFilter
              start_date={start_date}
              end_date={end_date}
              office_id={office_id}
              setStart_date={setStart_date}
              setEnd_date={setEnd_date}
              setOffice_id={setOffice_id}
              offices={offices}
              collectsOffice={collectsOffice}
              selectedCollects={selectedCollects}
              setSelectedCollects={setSelectedCollects}
              selectAll={selectAll}
              setSelectAll={setSelectAll}
              handleCheckboxChange={handleCheckboxChange}
              handleSelectAll={handleSelectAll}
              disableCollectFilters={filtersDisabledForTodo}
              disableOfficeSelect={filtersDisabledForTodo}
              allOfficesAndCollectsCheckbox={
                authResolved
                  ? {
                      checked: todo,
                      onChange: handleTodoChange,
                      label: "Todas las oficinas y cobros",
                      disabled: !isSuperAdmin,
                    }
                  : undefined
              }
            />
            <Divider sx={{ my: 1 }} />
          </CardContent>
        </form>

        <CardContent sx={{ pt: 0 }}>
          <PageScrollArea sx={{ height: 380 }}>
            {rows.length > 0 || dates.length > 0 ? (
              <SimpleCustomTable
                rows={rows}
                columns={columns}
                maxHeight={500}
                showBorders
                showCount
                exportCsv
                exportFileName="informe_transmision.csv"
                wrapCellContent
                columnGroups={columnGroups}
              />
            ) : loading ? (
              <Box sx={{ py: 6, textAlign: "center", color: "text.secondary" }}>
                Cargando...
              </Box>
            ) : (
              <EmptyStateMessage message="No existen registros" align="left" />
            )}
          </PageScrollArea>
        </CardContent>
      </Card>
    </HeaderResponsive>
  );
};

export default ReporteTransmision;
